<template>
  <div class="dashboard-data-quality">
    <!-- Header -->
    <div class="dq-header">
      <h3 class="dq-title">{{ $t('org-dashboard.data-quality') }}</h3>
      <span class="dq-status-badge" :class="'dq-status-badge--' + status">{{ statusLabel }}</span>
    </div>

    <!-- Main: Donut + Breakdown -->
    <div class="dq-main">
      <div class="dq-donut-section">
        <div class="dq-donut-wrap">
          <highcharts v-if="donutOptions" :options="donutOptions" />
          <div class="dq-donut-center">
            <span class="dq-donut-value">{{ score }}%</span>
          </div>
        </div>
        <div class="dq-breakdown">
          <div class="dq-breakdown-title">{{ $t('org-dashboard.data-integrity') }}</div>
          <div
            v-for="item in breakdownItems"
            :key="item.key"
            class="dq-breakdown-item"
          >
            <span class="dq-breakdown-dot" :style="{ backgroundColor: item.color }"></span>
            <span class="dq-breakdown-label">{{ item.label }}</span>
            <span class="dq-breakdown-count" :class="{ 'dq-breakdown-count--active': item.count > 0 }">{{ item.count }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Compact list -->
    <div class="dq-list-section">
      <div class="dq-list-header">
        <h4 class="dq-section-title">
          {{ $t('org-dashboard.dq-problematic-title') }}
          <span v-if="totalProblematic > 0" class="dq-badge">{{ totalProblematic }}</span>
        </h4>
        <v-btn v-if="totalProblematic > 0" x-small text color="primary" @click="$emit('view-all')">
          {{ $t('org-dashboard.dq-view-all') }}
        </v-btn>
      </div>
      <table v-if="topProblematic.length" class="dq-table">
        <thead>
          <tr>
            <th>{{ $t('org-dashboard.dq-col-identifier') }}</th>
            <th>{{ $t('org-dashboard.dq-col-issue') }}</th>
            <th>{{ $t('org-dashboard.dq-col-duration') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in topProblematic"
            :key="item.id"
            class="dq-table-row"
            @click="$emit('charger-click', item.id)"
          >
            <td class="dq-table-id">{{ item.identifier }}</td>
            <td>{{ item.issue }}</td>
            <td>{{ item.duration }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else class="dq-no-data">{{ $t('org-dashboard.dq-no-issues') }}</div>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

export default {
  name: 'DashboardDataQuality',
  components: {
    highcharts: Chart,
  },
  props: {
    score: {
      type: Number,
      default: 0,
    },
    status: {
      type: String,
      default: 'good',
    },
    breakdown: {
      type: Object,
      default: () => ({ heartbeatMissing: 0, inconsistentStatus: 0, evseNoState: 0 }),
    },
    totalProblematic: {
      type: Number,
      default: 0,
    },
    topProblematic: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    statusLabel() {
      const map = {
        good: this.$t('org-dashboard.status-good'),
        warning: this.$t('org-dashboard.status-warning'),
        critical: this.$t('org-dashboard.status-critical'),
      }
      return map[this.status] || this.status
    },

    scoreColor() {
      if (this.score >= 90) return '#22c55e'
      if (this.score >= 70) return '#eab308'
      return '#ef4444'
    },

    breakdownItems() {
      return [
        {
          key: 'heartbeatMissing',
          label: this.$t('org-dashboard.dq-heartbeat-missing'),
          count: this.breakdown.heartbeatMissing || 0,
          color: (this.breakdown.heartbeatMissing || 0) > 0 ? '#ef4444' : '#d1d5db',
        },
        {
          key: 'inconsistentStatus',
          label: this.$t('org-dashboard.dq-inconsistent-status'),
          count: this.breakdown.inconsistentStatus || 0,
          color: (this.breakdown.inconsistentStatus || 0) > 0 ? '#ef4444' : '#d1d5db',
        },
        {
          key: 'evseNoState',
          label: this.$t('org-dashboard.dq-evse-no-state'),
          count: this.breakdown.evseNoState || 0,
          color: (this.breakdown.evseNoState || 0) > 0 ? '#ef4444' : '#d1d5db',
        },
      ]
    },

    donutOptions() {
      const rest = 100 - this.score
      return {
        chart: {
          type: 'pie',
          height: 180,
          width: 180,
          backgroundColor: 'transparent',
          margin: [0, 0, 0, 0],
        },
        title: { text: '' },
        credits: { enabled: false },
        tooltip: { enabled: false },
        plotOptions: {
          pie: {
            innerSize: '68%',
            dataLabels: { enabled: false },
            borderWidth: 0,
            states: { hover: { brightness: 0 } },
            enableMouseTracking: false,
          },
        },
        series: [{
          data: [
            { y: this.score, color: this.scoreColor },
            { y: rest > 0 ? rest : 0, color: '#f3f4f6' },
          ],
        }],
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-data-quality {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.dq-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 12px;
}

.dq-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.dq-status-badge {
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;

  &--good {
    background: #ecfdf5;
    color: #059669;
  }
  &--warning {
    background: #fffbeb;
    color: #d97706;
  }
  &--critical {
    background: #fef2f2;
    color: #dc2626;
  }
}

/* Main: Donut + Breakdown */
.dq-main {
  margin-bottom: 16px;
}

.dq-donut-section {
  display: flex;
  align-items: center;
  gap: 24px;
}

.dq-donut-wrap {
  flex-shrink: 0;
  position: relative;
}

.dq-donut-center {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.dq-donut-value {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.dq-breakdown {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dq-breakdown-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 2px;
}

.dq-breakdown-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #4b5563;
}

.dq-breakdown-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dq-breakdown-label {
  flex: 1;
}

.dq-breakdown-count {
  font-weight: 700;
  color: #9ca3af;

  &--active {
    color: #ef4444;
  }
}

/* Compact list */
.dq-list-section {
  border-top: 1px solid #f3f4f6;
  padding-top: 16px;
}

.dq-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.dq-section-title {
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 6px;
}

.dq-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #fef2f2;
  color: #ef4444;
  font-size: 10px;
  font-weight: 700;
}

.dq-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;

  th {
    text-align: left;
    font-weight: 600;
    color: #6b7280;
    padding: 6px 8px;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
  }

  td {
    padding: 6px 8px;
    color: #374151;
    border-bottom: 1px solid #f3f4f6;
  }
}

.dq-table-row {
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #fef2f2;
  }
}

.dq-table-id {
  font-weight: 600;
  color: #1976d2;
}

.dq-no-data {
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
  padding: 16px;
}
</style>
